import { storage } from './storage.js';
import { sound } from './audioService.js';

export const STAT_DEFINITIONS = {
  food: {
    id: 'food',
    name: 'Food',
    icon: '🍔',
    description: 'Keep your buddy well fed with healthy snacks!',
    gameTitle: 'Nom-Nom Catch',
    gameSubtitle: 'Catch falling tasty treats & dodge bombs!',
    color: '#FF6B6B',
    bgGradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
    decayIntervalSec: 7200 // 1 bar every 2 hours (twice as fast as standard 4h)
  },
  fun: {
    id: 'fun',
    name: 'Fun',
    icon: '🎮',
    description: 'Keep spirits high with bouncy entertainment!',
    gameTitle: 'Balloon Pop Blitz',
    gameSubtitle: 'Pop matching bouncy balloons before they burst!',
    color: '#4D96FF',
    bgGradient: 'linear-gradient(135deg, #4D96FF 0%, #6BCB77 100%)',
    decayIntervalSec: 14400 // 1 bar every 4 hours
  },
  work: {
    id: 'work',
    name: 'Work',
    icon: '💼',
    description: 'Train focus, memory, and cognitive sharpness!',
    gameTitle: 'Memory Matrix',
    gameSubtitle: 'Repeat sequence patterns & organize tasks!',
    color: '#FFD93D',
    bgGradient: 'linear-gradient(135deg, #F9D423 0%, #FF4E50 100%)',
    decayIntervalSec: 14400 // 1 bar every 4 hours
  },
  sleep: {
    id: 'sleep',
    name: 'Sleep',
    icon: '💤',
    description: 'Recharge vitality and enter dreamland!',
    gameTitle: 'Dream Cloud Hopper',
    gameSubtitle: 'Hop on fluffy clouds & catch falling stars!',
    color: '#9D4EDD',
    bgGradient: 'linear-gradient(135deg, #7B2CBF 0%, #C77DFF 100%)',
    decayIntervalSec: 7200 // 1 bar every 2 hours (twice as fast as standard 4h)
  },
  social: {
    id: 'social',
    name: 'Social',
    icon: '💬',
    description: 'Connect with friends and share heartfelt emotes!',
    gameTitle: 'Heart & Emote Link',
    gameSubtitle: 'Connect floating matching emotes swiftly!',
    color: '#FF4081',
    bgGradient: 'linear-gradient(135deg, #FF4081 0%, #FFA07A 100%)',
    decayIntervalSec: 14400 // 1 bar every 4 hours
  },
  fitness: {
    id: 'fitness',
    name: 'Fitness',
    icon: '🏃',
    description: 'Cardio exercise and agility training!',
    gameTitle: 'Hurdle Dash Sprint',
    gameSubtitle: 'Jump hurdles and collect energy power-ups!',
    color: '#00C853',
    bgGradient: 'linear-gradient(135deg, #00E676 0%, #00B0FF 100%)',
    decayIntervalSec: 14400 // 1 bar every 4 hours
  }
};

export const MAX_BARS = 9;
export const BARS_PER_WIN = 3;

class GameStateManager {
  constructor() {
    this.listeners = new Set();
    this.timerId = null;
    this.petName = 'Wife';
    this.stats = {};
    this.lastSavedTimestamp = Date.now();
    this.fastDecayMode = false; // Debug fast decay toggle (5s for food/sleep, 10s for others)
    this.init();
  }

  getIntervalSec(statId) {
    const def = STAT_DEFINITIONS[statId];
    if (this.fastDecayMode) {
      return def.decayIntervalSec === 7200 ? 5 : 10;
    }
    return def.decayIntervalSec;
  }

  init() {
    const saved = storage.get();
    const now = Date.now();

    if (saved && saved.stats) {
      this.petName = saved.petName || 'Wife';
      this.lastSavedTimestamp = saved.lastSavedTimestamp || now;
      this.stats = {};

      // Initialize or load stats
      Object.keys(STAT_DEFINITIONS).forEach((statId) => {
        if (saved.stats[statId] !== undefined) {
          const statData = saved.stats[statId];
          this.stats[statId] = {
            bars: typeof statData.bars === 'number' ? statData.bars : MAX_BARS,
            lastDecayTimestamp: statData.lastDecayTimestamp || this.lastSavedTimestamp
          };
        } else {
          this.stats[statId] = {
            bars: MAX_BARS,
            lastDecayTimestamp: now
          };
        }
      });

      // Calculate offline decay between last save and now
      this.calculateOfflineDecay(now);
    } else {
      // Default fresh state: start with high stats (8/9 bars each)
      this.petName = 'Wife';
      this.lastSavedTimestamp = now;
      this.stats = {};
      Object.keys(STAT_DEFINITIONS).forEach((statId) => {
        this.stats[statId] = {
          bars: 8,
          lastDecayTimestamp: now
        };
      });
      this.save();
    }

    // Start background tick loop (every 1 second)
    this.startTickLoop();
  }

  calculateOfflineDecay(now) {
    let changed = false;
    Object.keys(STAT_DEFINITIONS).forEach((statId) => {
      const stat = this.stats[statId];
      const intervalMs = this.getIntervalSec(statId) * 1000;
      const elapsedMs = now - stat.lastDecayTimestamp;

      if (elapsedMs >= intervalMs && stat.bars > 0) {
        const barsLost = Math.min(stat.bars, Math.floor(elapsedMs / intervalMs));
        if (barsLost > 0) {
          stat.bars = Math.max(0, stat.bars - barsLost);
          stat.lastDecayTimestamp = stat.lastDecayTimestamp + barsLost * intervalMs;
          changed = true;
        }
      }
    });

    if (changed) {
      this.save();
    }
  }

  startTickLoop() {
    if (this.timerId) clearInterval(this.timerId);
    this.timerId = setInterval(() => {
      this.tick();
    }, 1000);
  }

  tick() {
    const now = Date.now();
    let stateChanged = false;

    Object.keys(STAT_DEFINITIONS).forEach((statId) => {
      const stat = this.stats[statId];
      const intervalMs = this.getIntervalSec(statId) * 1000;
      const elapsedMs = now - stat.lastDecayTimestamp;

      if (elapsedMs >= intervalMs && stat.bars > 0) {
        stat.bars = Math.max(0, stat.bars - 1);
        stat.lastDecayTimestamp = now;
        stateChanged = true;
      }
    });

    if (stateChanged) {
      this.save();
    }
    this.notify();
  }

  // Get seconds remaining until next bar loss for a stat
  getTimeToNextDecay(statId) {
    const stat = this.stats[statId];
    if (!stat || stat.bars <= 0) return 0;

    const intervalSec = this.getIntervalSec(statId);
    const elapsedSec = (Date.now() - stat.lastDecayTimestamp) / 1000;
    const remaining = Math.max(0, Math.ceil(intervalSec - elapsedSec));
    return remaining;
  }

  // Calculate overall average percentage
  getAveragePercentage() {
    const statKeys = Object.keys(STAT_DEFINITIONS);
    if (statKeys.length === 0) return 100;

    let totalBars = 0;
    let maxPossibleBars = statKeys.length * MAX_BARS;

    statKeys.forEach((key) => {
      totalBars += this.stats[key] ? this.stats[key].bars : 0;
    });

    return Math.round((totalBars / maxPossibleBars) * 100);
  }

  // Determine current mood state:
  // > 80%: happy (Bright Green)
  // 50% - 80%: serene (Lime/Spring Green)
  // 20% - 49%: sad (Amber/Orange)
  // < 20%: mad (Bright Red)
  getMood() {
    const avg = this.getAveragePercentage();
    if (avg > 80) {
      return {
        id: 'happy',
        title: 'Happy & Joyful',
        emoji: '✨😄✨',
        description: 'Jumping around full of energy & joy!',
        themeColor: '#00E676',
        textColor: '#00E676',
        badgeBg: 'rgba(0, 230, 118, 0.2)'
      };
    } else if (avg >= 50) {
      return {
        id: 'serene',
        title: 'Mildly Serene',
        emoji: '🌿😌',
        description: 'Calm, relaxed, and feeling comfortable.',
        themeColor: '#AEEA00',
        textColor: '#AEEA00',
        badgeBg: 'rgba(174, 234, 0, 0.2)'
      };
    } else if (avg >= 20) {
      return {
        id: 'sad',
        title: 'Sad & Droopy',
        emoji: '💧🥺',
        description: 'Feeling lonely and neglected. Needs care!',
        themeColor: '#FF9100',
        textColor: '#FF9100',
        badgeBg: 'rgba(255, 145, 0, 0.2)'
      };
    } else {
      return {
        id: 'mad',
        title: 'Mad & Grumpy',
        emoji: '🔥😡',
        description: 'Dangerously low stats! Fuming with anger!',
        themeColor: '#FF1744',
        textColor: '#FF1744',
        badgeBg: 'rgba(255, 23, 68, 0.2)'
      };
    }
  }

  // Reward +3 bars on winning a stat mini-game
  rewardStat(statId, barsToAdd = BARS_PER_WIN) {
    if (!this.stats[statId]) return 0;
    const oldBars = this.stats[statId].bars;
    const newBars = Math.min(MAX_BARS, oldBars + barsToAdd);
    const added = newBars - oldBars;

    this.stats[statId].bars = newBars;
    this.stats[statId].lastDecayTimestamp = Date.now(); // reset decay countdown

    this.save();
    this.notify();

    sound.playBarGain();
    return added;
  }

  // Time-warp simulation for testing
  timeWarp(hoursToAdd = 1) {
    const msToAdd = hoursToAdd * 60 * 60 * 1000;
    Object.keys(STAT_DEFINITIONS).forEach((statId) => {
      if (this.stats[statId]) {
        this.stats[statId].lastDecayTimestamp -= msToAdd;
      }
    });
    this.calculateOfflineDecay(Date.now());
    this.notify();
  }

  // Quick helper to fill all stats
  fillAllStats() {
    Object.keys(STAT_DEFINITIONS).forEach((statId) => {
      if (this.stats[statId]) {
        this.stats[statId].bars = MAX_BARS;
        this.stats[statId].lastDecayTimestamp = Date.now();
      }
    });
    this.save();
    this.notify();
    sound.playVictory();
  }

  // Quick helper to drain stats to test Sad or Mad states
  drainToAverage(targetPercentage) {
    const totalBarsTarget = Math.round((targetPercentage / 100) * (Object.keys(STAT_DEFINITIONS).length * MAX_BARS));
    const count = Object.keys(STAT_DEFINITIONS).length;
    const basePerStat = Math.floor(totalBarsTarget / count);
    let remainder = totalBarsTarget % count;

    Object.keys(STAT_DEFINITIONS).forEach((statId) => {
      let b = basePerStat + (remainder > 0 ? 1 : 0);
      if (remainder > 0) remainder--;
      this.stats[statId].bars = Math.max(0, Math.min(MAX_BARS, b));
      this.stats[statId].lastDecayTimestamp = Date.now();
    });
    this.save();
    this.notify();
  }

  toggleFastDecay() {
    this.fastDecayMode = !this.fastDecayMode;
    this.notify();
    return this.fastDecayMode;
  }

  save() {
    this.lastSavedTimestamp = Date.now();
    storage.set({
      petName: this.petName,
      lastSavedTimestamp: this.lastSavedTimestamp,
      stats: this.stats
    });
  }

  subscribe(listener) {
    this.listeners.add(listener);
    // Initial call
    listener(this.getStateSnapshot());
    return () => this.listeners.delete(listener);
  }

  notify() {
    const snapshot = this.getStateSnapshot();
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (e) {
        console.error('Error in gameState listener', e);
      }
    });
  }

  getStateSnapshot() {
    const avg = this.getAveragePercentage();
    const mood = this.getMood();

    const statsDetail = {};
    Object.keys(STAT_DEFINITIONS).forEach((statId) => {
      const def = STAT_DEFINITIONS[statId];
      const currentBars = this.stats[statId] ? this.stats[statId].bars : 0;
      const nextDecaySec = this.getTimeToNextDecay(statId);
      statsDetail[statId] = {
        ...def,
        bars: currentBars,
        maxBars: MAX_BARS,
        percentage: Math.round((currentBars / MAX_BARS) * 100),
        secondsToNextDecay: nextDecaySec
      };
    });

    return {
      petName: this.petName,
      stats: statsDetail,
      averagePercentage: avg,
      mood,
      fastDecayMode: this.fastDecayMode
    };
  }
}

export const gameState = new GameStateManager();
